import { useEffect, useRef } from "react";
import * as THREE from "three";

// Dual-orbit particle system — replicates the prototype hero "moving colored dots".
export default function Orbits() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const reduced = window.matchMedia && matchMedia("(prefers-reduced-motion: reduce)").matches;
    const canvas = canvasRef.current;
    if (reduced || !canvas) return;

    const renderer = new THREE.WebGLRenderer({ canvas, alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.8));
    const scene = new THREE.Scene();
    const cam = new THREE.PerspectiveCamera(50, 1, 0.1, 100);
    cam.position.z = 9;

    function makeRing(radius, count, color, size) {
      const g = new THREE.BufferGeometry();
      const pos = new Float32Array(count * 3);
      for (let i = 0; i < count; i++) {
        const a = Math.random() * Math.PI * 2;
        const r = radius + (Math.random() - 0.5) * 0.5;
        pos[i * 3] = Math.cos(a) * r;
        pos[i * 3 + 1] = Math.sin(a) * r * 0.42 + (Math.random() - 0.5) * 0.3;
        pos[i * 3 + 2] = (Math.random() - 0.5) * 1.4;
      }
      g.setAttribute("position", new THREE.BufferAttribute(pos, 3));
      const m = new THREE.PointsMaterial({ color, size, transparent: true, opacity: 0.85, depthWrite: false });
      return new THREE.Points(g, m);
    }

    const ring1 = makeRing(3.1, 520, 0x185fa5, 0.045); // pine
    const ring2 = makeRing(2.3, 420, 0x1d9e75, 0.05);  // green
    ring1.rotation.z = 0.5;
    ring2.rotation.z = -0.55;
    const group = new THREE.Group();
    group.add(ring1, ring2);
    const dust = makeRing(1.2, 180, 0xb5d4f4, 0.04);   // soft-blue dust
    group.add(dust);
    scene.add(group);
    group.position.x = 2.4;

    let mx = 0, my = 0;
    const onMove = (e) => { mx = e.clientX / innerWidth - 0.5; my = e.clientY / innerHeight - 0.5; };
    addEventListener("pointermove", onMove, { passive: true });

    function size() {
      const p = canvas.parentElement;
      if (!p) return;
      const w = p.clientWidth, h = p.clientHeight;
      if (w === 0 || h === 0) return;
      renderer.setSize(w, h, false);
      cam.aspect = w / h;
      cam.updateProjectionMatrix();
      group.position.x = w < 1080 ? 0 : 2.4;
    }
    size();
    addEventListener("resize", size);

    let t = 0, raf;
    function loop() {
      t += 0.004;
      ring1.rotation.y = t * 0.9; ring1.rotation.x = Math.sin(t * 0.6) * 0.12;
      ring2.rotation.y = -t * 1.15; ring2.rotation.x = Math.cos(t * 0.5) * 0.15;
      dust.rotation.y = t * 0.4;
      group.rotation.x += (my * 0.35 - group.rotation.x) * 0.04;
      group.rotation.z += (mx * 0.25 - group.rotation.z) * 0.04;
      renderer.render(scene, cam);
      raf = requestAnimationFrame(loop);
    }
    loop();
    const onVis = () => { if (document.hidden) cancelAnimationFrame(raf); else loop(); };
    document.addEventListener("visibilitychange", onVis);

    return () => {
      cancelAnimationFrame(raf);
      removeEventListener("pointermove", onMove);
      removeEventListener("resize", size);
      document.removeEventListener("visibilitychange", onVis);
      renderer.dispose();
    };
  }, []);

  return <canvas id="orbits" ref={canvasRef} aria-hidden="true" />;
}
